import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Package, ArrowRight, Home, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { formatPrice } from '@/lib/formatMoney';
import { useAuth } from '@/context/AuthContext';

interface CheckoutData {
  id: string;
  producer_id: string;
  customer_email: string;
  customer_name: string | null;
  total_amount: number;
  shipping_label: string | null;
  items: Array<{
    title: string;
    quantity: number;
    unit_amount: number;
  }>;
  order_id: string | null;
  status: string;
}

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  // Token-based lookup for success/cancel redirects (preferred, no auth required)
  const token = searchParams.get('token');
  // Legacy checkout_id lookup (requires auth + ownership)
  const checkoutId = searchParams.get('checkout');
  const { user } = useAuth();
  
  const [checkout, setCheckout] = useState<CheckoutData | null>(null);
  const [producerName, setProducerName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchCheckout() {
      // Prefer token-based lookup, fallback to checkout_id
      if (!token && !checkoutId) {
        console.warn('CheckoutSuccessPage: no token or checkout_id in URL');
        setError('Ingen beställning hittades');
        setIsLoading(false);
        return;
      }

      try {
        // Use token if available (for Stripe redirects), otherwise use checkout_id
        const requestBody = token 
          ? { token } 
          : { checkout_id: checkoutId };

        console.log('CheckoutSuccessPage: fetching checkout', { 
          lookupField: token ? 'token' : 'checkout_id',
          lookupValue: token || checkoutId,
        });

        const { data, error: invokeError } = await supabase.functions.invoke('get-checkout', {
          body: requestBody,
        });

        if (invokeError) {
          console.error('Error fetching checkout via function:', invokeError);
          setError('Kunde inte hämta beställningsinformation');
          setIsLoading(false);
          return;
        }

        const payload = data as { checkout: CheckoutData | null; producer_name?: string } | null;
        if (!payload?.checkout) {
          console.warn('Checkout not found (or access denied)', {
            lookupField: token ? 'token' : 'checkout_id',
            lookupValue: token || checkoutId,
          });
          setError('Beställningen hittades inte eller har gått ut.');
          setIsLoading(false);
          return;
        }

        setCheckout(payload.checkout);
        setProducerName(payload.producer_name ?? '');
      } catch (err) {
        console.error('Error:', err);
        setError('Ett fel uppstod');
      } finally {
        setIsLoading(false);
      }
    }

    fetchCheckout();
  }, [token, checkoutId]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !checkout) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Något gick fel</CardTitle>
            <CardDescription>{error || 'Kassan hittades inte eller har gått ut.'}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 items-center">
            <Button asChild variant="default">
              <Link to="/explore">
                <ArrowRight className="h-4 w-4 mr-2" />
                Tillbaka till butiken
              </Link>
            </Button>
            <Button asChild variant="ghost" size="sm">
              <Link to="/">
                <Home className="h-4 w-4 mr-2" />
                Till startsidan
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const items = checkout.items as Array<{ title: string; quantity: number; unit_amount: number }>;

  const isPaid = checkout.status === 'paid' || checkout.status === 'completed';

  return (
    <div className="min-h-screen bg-muted/30 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Success header */}
        <Card className="border-green-200 bg-green-50/50">
          <CardHeader className="text-center pb-4">
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </div>
            <CardTitle className="text-2xl text-green-800">Tack för din beställning!</CardTitle>
            <CardDescription className="text-green-700">
              {isPaid
                ? `En bekräftelse har skickats till ${checkout.customer_email}`
                : `Din beställning har skickats till producenten. En bekräftelse skickas till ${checkout.customer_email}`}
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Order details */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-lg">Beställningsdetaljer</CardTitle>
            </div>
            {producerName && (
              <CardDescription>Från {producerName}</CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Items */}
            <div className="space-y-2">
              {items.map((item, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span>
                    {item.title} × {item.quantity}
                  </span>
                  <span className="font-medium">
                    {formatPrice((item.unit_amount * item.quantity) / 100, 'SEK')}
                  </span>
                </div>
              ))}
            </div>

            <Separator />

            {/* Delivery */}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Leverans</span>
              <span>{checkout.shipping_label || 'Upphämtning'}</span>
            </div>

            <Separator />

            {/* Total */}
            <div className="flex justify-between font-semibold text-lg">
              <span>{isPaid ? 'Totalt betalt' : 'Totalt'}</span>
              <span>{formatPrice(checkout.total_amount / 100, 'SEK')}</span>
            </div>
          </CardContent>
        </Card>

        {/* Next steps */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Vad händer nu?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-3">
              <div className="h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium shrink-0">
                1
              </div>
              <div>
                <p className="font-medium">Producenten förbereder din order</p>
                <p className="text-sm text-muted-foreground">
                  Du får ett meddelande när ordern är klar för upphämtning
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium shrink-0">
                2
              </div>
              <div>
                <p className="font-medium">Hämta din beställning</p>
                <p className="text-sm text-muted-foreground">
                  Kontakta producenten för att boka en tid som passar
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          {user && (
            <Button asChild variant="outline" className="flex-1">
              <Link to="/app/customer/orders">
                <Package className="h-4 w-4 mr-2" />
                Mina ordrar
              </Link>
            </Button>
          )}
          <Button asChild className="flex-1">
            <Link to="/explore">
              Utforska fler gårdar
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
